import {
  WorkoutExerciseMetric,
  Workout,
  WorkoutSet,
  WorkoutExercise,
} from '@strength/domain-workout';
import { DateTime } from 'luxon';
import { WorkoutWithCycleAndSession } from '../../types';
import { ActualWorkout } from '../types';

export const BOWLING_METRICS = ['Match', 'Training'] as const;
export type BowlingMetricKeyword = (typeof BOWLING_METRICS)[number];

export const getRepsValueForMetricKeyword = (
  metricKeyword: string,
  prescriptionWorkout: Workout | undefined,
): {
  value?: string;
  metric?: WorkoutExerciseMetric;
  workoutExercise?: WorkoutExercise;
  workoutSet?: WorkoutSet;
} => {
  const workoutId = prescriptionWorkout?.workoutId;
  if (!workoutId) {
    return {};
  }

  // find the Bowling exercise in the Bowling section
  const section = prescriptionWorkout.sections.find(
    (s) => s.name === 'Bowling',
  );
  const exercise = section?.entries.find(
    (e) => e.__type === 'WorkoutExercise' && e.exerciseName === 'Bowling',
  );

  if (exercise?.__type !== 'WorkoutExercise') {
    return {};
  }

  // get the Delivery Type metric id
  const deliveryTypeMetricId =
    exercise?.__type === 'WorkoutExercise'
      ? exercise.metrics.find((m) => m.metricName === 'Delivery Type')?.metricId
      : undefined;

  // get the Reps metric id
  const repsMetric =
    exercise?.__type === 'WorkoutExercise'
      ? exercise.metrics.find((m) => m.metricName === 'Reps')
      : undefined;
  const repsMetricId = repsMetric?.metricId;

  const set =
    exercise?.__type === 'WorkoutExercise'
      ? exercise.sets.find((s) =>
          s.metrics.find(
            (m) =>
              m.metricId === deliveryTypeMetricId && m.input === metricKeyword,
          ),
        )
      : undefined;

  return {
    value: set?.metrics.find((m) => m.metricId === repsMetricId)?.input,
    metric: repsMetric,
    workoutExercise: exercise,
    workoutSet: set,
  };
};

/**
 * Picks the provenance of the most recent prescription value on a day across
 * all metrics of every workout's sets — used for the day-header "Last
 * Updated" hover. Workout order is not meaningful, so the latest is chosen
 * by enteredOn instant, not by position. Falls back to the other field when
 * only one is stamped (older rows can carry enteredBy without enteredOn).
 */
export function getLatestPrescriptionProvenance(
  workouts: Workout[] | undefined,
): {
  enteredBy?: string;
  enteredOn?: string;
} {
  const metrics = (workouts ?? [])
    .flatMap((w) => w.sections)
    .flatMap((s) =>
      s.entries.flatMap((e) => (e.__type === 'WorkoutExercise' ? e.sets : [])),
    )
    .flatMap((set) => set.metrics);

  // Compare enteredOn by instant, not lexicographically — the domain schema
  // allows offset-aware ISO timestamps whose string order differs from time
  // order (e.g. +14:00 vs -12:00 offsets).
  const withEnteredOn = metrics
    .filter((m): m is typeof m & { enteredOn: string } => !!m.enteredOn)
    .map((m) => ({ metric: m, at: DateTime.fromISO(m.enteredOn).toMillis() }))
    .filter(({ at }) => !Number.isNaN(at))
    .sort((a, b) => a.at - b.at);
  const latest = withEnteredOn.at(-1);
  if (latest) {
    return {
      enteredBy: latest.metric.enteredBy,
      enteredOn: latest.metric.enteredOn,
    };
  }

  // Rows can carry enteredBy without enteredOn — fall back to that.
  const enteredByOnly = metrics.find((m) => m.enteredBy);
  if (enteredByOnly) return { enteredBy: enteredByOnly.enteredBy };
  return {};
}

function parseBowlCount(value: string | undefined): number {
  if (value === undefined) return 0;
  const n = Number(value);
  return Number.isNaN(n) ? 0 : n;
}

function buildBowlCountsByType(
  workout: Workout | undefined,
): Record<BowlingMetricKeyword, number> {
  return Object.fromEntries(
    BOWLING_METRICS.map((kw) => {
      const { value } = getRepsValueForMetricKeyword(kw, workout);
      return [kw, parseBowlCount(value)];
    }),
  ) as Record<BowlingMetricKeyword, number>;
}

export function getPrescriptionBowlCounts(
  workout: WorkoutWithCycleAndSession,
): {
  planned: number;
  byType: Record<BowlingMetricKeyword, number>;
} {
  const byType = buildBowlCountsByType(workout);
  return {
    planned: Object.values(byType).reduce((sum, v) => sum + v, 0),
    byType,
  };
}

export function getActualBowlCounts(workout: ActualWorkout): {
  actual: number | undefined;
  byType: Record<BowlingMetricKeyword, number> | undefined;
} {
  if (!workout.hasActual) return { actual: undefined, byType: undefined };
  const byType = buildBowlCountsByType(workout.workout);
  return {
    actual: Object.values(byType).reduce((sum, v) => sum + v, 0),
    byType,
  };
}
